import type MapView from "@arcgis/core/views/MapView.js";

import {
  clearPendingAuthAttempt,
  configureArcgis,
  createMap,
  hasPendingAuthAttempt,
  loadDashboardData,
  registerOAuth,
  requireCredential,
  signIn,
  signOut
} from "./arcgis";
import { loadConfig } from "./config";
import {
  renderDashboard,
  setAuthState,
  setAuthUnavailable,
  setAuthPrompt,
  setBusy,
  setError,
  setScenarioLinkStatus,
  wireNavigationMenu,
  wireDashboardPanelControls,
  wireStaticIcons,
  wireTooltips
} from "./ui";
import {
  percentToWeight,
  readScenarioState,
  scenarioUrl,
  writeScenarioState
} from "./scenario";
import type { DashboardConfig, DashboardData, ScenarioOverride } from "./types";

let config: DashboardConfig | null = null;
let mapView: MapView | null = null;
let scenarioOverride: ScenarioOverride = readScenarioState();
let selectedRouteId: string | null = scenarioOverride.routeId;
let latestData: DashboardData | null = null;
let refreshTimer = 0;
let signedIn = false;

function htmlElement(id: string): HTMLElement {
  const found = document.getElementById(id);
  if (found === null) {
    throw new Error(`Missing element #${id}`);
  }
  return found;
}

function htmlButton(id: string): HTMLButtonElement {
  const found = document.getElementById(id);
  if (!(found instanceof HTMLButtonElement)) {
    throw new Error(`Missing button #${id}`);
  }
  return found;
}

function htmlSelect(id: string): HTMLSelectElement {
  const found = document.getElementById(id);
  if (!(found instanceof HTMLSelectElement)) {
    throw new Error(`Missing select #${id}`);
  }
  return found;
}

function htmlInput(id: string): HTMLInputElement {
  const found = document.getElementById(id);
  if (!(found instanceof HTMLInputElement)) {
    throw new Error(`Missing input #${id}`);
  }
  return found;
}

function configurationMessage(error: unknown): string {
  const detail = error instanceof Error ? error.message : "Dashboard configuration failed.";
  if (detail.includes("not valid JSON")) {
    return `${detail} Fix the file or recreate it from public/config.example.json, then reload this page.`;
  }
  return "Live ArcGIS settings are not configured yet. Create public/config.json from public/config.example.json, set the ArcGIS portal, OAuth app ID, web map, and service URLs, then register this page URL as an OAuth redirect URL.";
}

function recordValue(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  if (typeof value === "string" && value.trim()) {
    return `${key}=${value.trim()}`;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${key}=${value}`;
  }
  return null;
}

function authErrorDetails(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  if (typeof error !== "object" || error === null) {
    return "ArcGIS did not return a stored browser credential.";
  }

  const record = error as Record<string, unknown>;
  const details = typeof record.details === "object" && record.details !== null ? (record.details as Record<string, unknown>) : {};
  const fields = [
    recordValue(record, "name"),
    recordValue(record, "message"),
    recordValue(record, "error"),
    recordValue(record, "error_description"),
    recordValue(details, "messageCode"),
    recordValue(details, "httpStatus"),
    recordValue(details, "message"),
    recordValue(details, "error"),
    recordValue(details, "error_description")
  ].filter((field): field is string => field !== null);

  return fields.length > 0 ? fields.join("; ") : "ArcGIS did not return a stored browser credential.";
}

function authReturnMessage(error: unknown): string {
  const details = authErrorDetails(error);
  const corsHint =
    details.includes("Failed to fetch") || details.includes("httpStatus=0") || details.includes("httpStatus: 0")
      ? " If this includes Failed to fetch or httpStatus=0, ask the ArcGIS organization admin to add this site's origin under Organization > Settings > Security > Allow origins."
      : "";
  return `ArcGIS sign-in returned, but the dashboard could not open a usable credential. ${details} Check that this exact dashboard URL is saved as a redirect URL on the same ArcGIS OAuth client ID used by the deployment, then try signing in again.${corsHint}`;
}

async function ensureMap(): Promise<void> {
  if (config === null || mapView !== null) {
    return;
  }
  const mapNode = htmlElement("map-view");
  if (!(mapNode instanceof HTMLDivElement)) {
    throw new Error("Map container is missing.");
  }
  mapView = await createMap(mapNode, config);
}

async function refreshDashboard(): Promise<void> {
  if (config === null || !signedIn) {
    return;
  }
  setBusy(true);
  try {
    await ensureMap();
    latestData = await loadDashboardData(config);
    renderCurrentDashboard();
  } catch (error) {
    const message = error instanceof Error ? error.message : "Dashboard refresh failed.";
    setError(message);
  } finally {
    setBusy(false);
  }
}

function renderCurrentDashboard(): void {
  if (latestData === null) {
    return;
  }
  selectedRouteId = renderDashboard(latestData, selectedRouteId, scenarioOverride);
}

function setScenarioOverride(nextOverride: ScenarioOverride): void {
  scenarioOverride = nextOverride;
  writeScenarioState(scenarioOverride);
  setScenarioLinkStatus("");
  renderCurrentDashboard();
}

function selectedScenarioState(): ScenarioOverride {
  if (selectedRouteId === null) {
    return { routeId: null, singaporeWeight: null };
  }
  return {
    routeId: selectedRouteId,
    singaporeWeight: scenarioOverride.routeId === selectedRouteId ? scenarioOverride.singaporeWeight : null
  };
}

function handleScenarioWeightChange(value: string): void {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || selectedRouteId === null) {
    return;
  }
  setScenarioOverride({
    routeId: selectedRouteId,
    singaporeWeight: percentToWeight(parsed)
  });
}

async function copyScenarioLink(): Promise<void> {
  const url = scenarioUrl(selectedScenarioState());
  try {
    await navigator.clipboard.writeText(url);
    setScenarioLinkStatus("Scenario link copied.");
  } catch {
    setScenarioLinkStatus("Copy failed. Use the browser address bar for this scenario URL.");
  }
}

async function handleAuthClick(): Promise<void> {
  if (config === null) {
    setError("Configuration is required before ArcGIS sign-in can start.");
    return;
  }
  if (signedIn) {
    signOut();
    signedIn = false;
    setAuthState(false);
    window.clearInterval(refreshTimer);
    return;
  }
  try {
    await signIn(config);
    signedIn = true;
    setAuthState(true);
    await refreshDashboard();
    startAutoRefresh();
  } catch (error) {
    const message = error instanceof Error ? error.message : "Sign-in failed.";
    setError(message);
  }
}

function startAutoRefresh(): void {
  if (config === null) {
    return;
  }
  window.clearInterval(refreshTimer);
  refreshTimer = window.setInterval(() => {
    void refreshDashboard();
  }, Math.max(config.refreshSeconds, 30) * 1000);
}

async function boot(): Promise<void> {
  wireStaticIcons();
  wireNavigationMenu();
  wireDashboardPanelControls();
  wireTooltips();

  htmlButton("auth-button").addEventListener("click", () => {
    void handleAuthClick();
  });
  htmlButton("cover-auth-button").addEventListener("click", () => {
    void handleAuthClick();
  });
  htmlButton("refresh-button").addEventListener("click", () => {
    void refreshDashboard();
  });
  htmlSelect("route-select").addEventListener("change", (event) => {
    const target = event.target;
    if (target instanceof HTMLSelectElement) {
      selectedRouteId = target.value;
      setScenarioOverride({ routeId: selectedRouteId, singaporeWeight: null });
    }
  });
  htmlInput("singapore-weight-slider").addEventListener("input", (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement) {
      handleScenarioWeightChange(target.value);
    }
  });
  htmlInput("singapore-weight-input").addEventListener("input", (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement) {
      handleScenarioWeightChange(target.value);
    }
  });
  htmlButton("scenario-reset-button").addEventListener("click", () => {
    if (selectedRouteId !== null) {
      setScenarioOverride({ routeId: selectedRouteId, singaporeWeight: null });
    }
  });
  htmlButton("copy-scenario-link-button").addEventListener("click", () => {
    void copyScenarioLink();
  });

  try {
    config = await loadConfig();
    configureArcgis(config);
    registerOAuth(config);
    const wasReturningFromAuth = hasPendingAuthAttempt(config);
    try {
      await requireCredential(config);
      signedIn = true;
      clearPendingAuthAttempt();
    } catch (error) {
      signedIn = false;
      if (wasReturningFromAuth) {
        clearPendingAuthAttempt();
        const message = authReturnMessage(error);
        setAuthPrompt("ArcGIS Sign-In Did Not Complete", message);
        setError(message);
      }
    }
    setAuthState(signedIn);
    if (signedIn) {
      await refreshDashboard();
      startAutoRefresh();
    }
  } catch (error) {
    const message = configurationMessage(error);
    htmlButton("refresh-button").disabled = true;
    htmlSelect("route-select").disabled = true;
    setAuthUnavailable(message);
    setError(message);
  }
}

void boot();
